// Ad swap's draft as the form keeps it (spec G14-ad-swap; no live form, AdSwap-2): the server
// holds the member's draft (`.../templates/ad-swap/draft`), so it survives the panel; the form
// reads it, keeps its edits there, and asks the server's pack reader, the source ad's reading,
// the swap director and the actor's still. As Pixar Talking Ad's (`pixar-draft.ts`), with one
// approval before the video and one still.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../api/errors";
import {
  getGetAdSwapDraftQueryKey,
  useApproveAdSwapScript,
  useGetAdSwapDraft,
  useRegenerateAdSwapActor,
  useResetAdSwapDraft,
  useWriteAdSwapDraft,
  useWriteAdSwapScript,
  writeAdSwapDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type { AdSwapDraft, AdSwapScript, PackshotProduct, TemplateSource } from "../../../api/generated/model";
import {
  AdSwapApproval,
  AdSwapDraftWrite,
  AdSwapInputs,
  AdSwapScriptAsk,
  type AdSwapDraftWriteOutput,
  type AdSwapInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";

/** The form as it stands: its picks and its script. */
export interface AdSwapForm {
  inputs: AdSwapInputsOutput;
  script: AdSwapScript | null;
}

/** The product's packshot as the run names it: the brand's product's picture, the upload, or the
 * card's example. */
export function packshotOf(product: PackshotProduct): TemplateSource {
  if (product.kind === "product") return { kind: "asset", asset_id: product.image_id };
  if (product.kind === "upload") return { kind: "asset", asset_id: product.asset_id };
  return product;
}

/** "Generate video"'s body: the source ad, the packshot, the actor's still, the approved script
 * and the parts it was planned in, at the form's resolution; null until the still is drawn. */
export function videoBody(draft: AdSwapDraft, resolution: AdSwapInputsOutput["resolution"]) {
  const inputs = AdSwapInputs.safeParse(draft.inputs);
  const actor = draft.stills.find((each) => each.name === "actor")?.output ?? null;
  if (draft.stage !== "cast" || !inputs.success || draft.script === null || draft.reading === null) return null;
  const { product, source_ad: sourceAd } = inputs.data;
  if (!product || !sourceAd || actor === null) return null;
  return {
    type: "video.template" as const,
    inputs: {
      template: "ad-swap" as const,
      source_ad: sourceAd,
      // The brand's product, for the library's filter; an upload names none.
      ...(product.kind === "product" ? { product_id: product.product_id } : {}),
      packshot: packshotOf(product),
      actor,
      script: draft.script,
      parts: draft.reading.parts,
      resolution,
    },
  };
}

/** The draft refetched while it works: every 2 s while a step works, and a moment after (a step's
 * end moves the draft a moment after its job ends). */
const POLL_MS = 2_000;
const SETTLE_MS = 6_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** Where the draft's work stands, as the progress line names it. */
export interface DraftWork {
  packing: boolean;
  reading: boolean;
  writing: boolean;
  drawing: boolean;
}

export function workOf(draft: AdSwapDraft, asked: string | null): DraftWork {
  const answering = asked === draft.updated_at && draft.stage === "script" && draft.problem === null;
  const before = draft.pack_director === "working" || draft.reading_director === "working";
  return {
    packing: draft.pack_director === "working",
    reading: draft.reading_director === "working",
    writing: draft.script_director === "working" || (answering && !before),
    drawing: draft.stills.some((each) => each.status === "working"),
  };
}

function working(work: DraftWork): boolean {
  return work.packing || work.reading || work.writing || work.drawing;
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: AdSwapDraft): AdSwapForm {
  const inputs = AdSwapInputs.safeParse(draft.inputs);
  return { inputs: inputs.success ? inputs.data : AdSwapInputs.parse({}), script: draft.script };
}

/** A refusal because the draft is locked (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it: the form shows the draft as read; edits in
 * the script stage, nothing working, are kept with PUT a moment after typing pauses; each ask
 * answers with the draft, shown from then on.
 */
export function useAdSwapDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetAdSwapDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<AdSwapForm | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const lastBusy = useRef(0);
  const read = useGetAdSwapDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        const poll = now - lastBusy.current < SETTLE_MS ? POLL_MS : false;
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  const took = (answer: AdSwapDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const script = useWriteAdSwapScript({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const approval = useApproveAdSwapScript(asks);
  const regeneration = useRegenerateAdSwapActor(asks);
  const reset = useResetAdSwapDraft(asks);
  const asking = script.isPending || approval.isPending || regeneration.isPending || reset.isPending;
  const work: DraftWork =
    draft === undefined ? { packing: false, reading: false, writing: false, drawing: false } : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "script" && local !== null ? local : server;
  const editable = draft?.stage === "script" && !busy;

  const save = useWriteAdSwapDraft({
    mutation: {
      onSuccess: () => setFailure(null),
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  const unsaved = useRef<{ teamId: string; brandId: string; data: AdSwapDraftWriteOutput } | null>(null);
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) writeAdSwapDraft(pending.teamId, pending.brandId, pending.data).catch(() => undefined);
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable) return undefined;
    const body = AdSwapDraftWrite.safeParse({ inputs: local.inputs, script: local.script });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    form,
    work,
    busy,
    editable,
    failure,
    approving: approval.isPending,
    resetting: reset.isPending,
    edit(change: Partial<AdSwapForm>) {
      if (server === null) return;
      setLocal((current) => ({ ...(current ?? server), ...change }));
    },
    editInputs(change: Partial<AdSwapInputsOutput>) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    generateScript() {
      if (form === null) return;
      const body = AdSwapScriptAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      script.mutate({ teamId, brandId, data: body.data });
    },
    approve() {
      if (form === null) return;
      const body = AdSwapApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      approval.mutate({ teamId, brandId, data: body.data });
    },
    regenerate() {
      ask();
      regeneration.mutate({ teamId, brandId });
    },
    startOver() {
      ask();
      setAsked(null);
      reset.mutate({ teamId, brandId });
    },
  };
}
